/**
 * Storage clearing functions with proper error handling and logging
 *
 * PRINCIPLE: Clear ALL storage for a category (localStorage + sessionStorage)
 * Users don't care about storage mechanism, they care about functionality
 */

import { indexedDBManager } from '../../services/IndexDBService';
import SentryService from '../../services/SentryService';
import { StorageService } from '../../services/storage/StorageService';
import type { StorageBackendType } from '../../services/storage/types';
import { dispatchClearActiveEncounter } from '../activeEncounterEvents';

import { REDUX_PERSIST_PATTERN, STORAGE_CATEGORIES } from './categories';
import { type ClearResult } from './types';

const LEGACY_RECORDING_SESSION_DB = 'SmartScribeRecordingSessions';

async function clearPermanentRecordingStorage(): Promise<StorageBackendType[]> {
  const clearedBackends = new Set<StorageBackendType>();

  for (const requestedBackend of ['opfs', 'indexeddb'] as const) {
    const storageService = new StorageService();
    try {
      await storageService.initialize(requestedBackend);
      const selectedBackend = storageService.getSnapshot().backendType;

      // OPFS initialization can fall back to IndexedDB. Avoid clearing that
      // fallback twice when the explicit IndexedDB pass runs next.
      if (selectedBackend && !clearedBackends.has(selectedBackend)) {
        await storageService.clearAllSessions();
        clearedBackends.add(selectedBackend);
      }
    } catch (error) {
      // Cleanup is best-effort across independent backends. A quota or
      // initialization failure in one must not block the next backend or
      // the legacy database cleanup that follows.
      console.warn(
        `Failed to clear permanent recording storage backend: ${requestedBackend}`,
        error,
      );
    } finally {
      storageService.dispose();
    }
  }

  return [...clearedBackends];
}

async function clearLegacyRecordingStorage(): Promise<void> {
  await indexedDBManager.clearAll();

  await new Promise<void>((resolve, reject) => {
    const deleteReq = indexedDB.deleteDatabase(LEGACY_RECORDING_SESSION_DB);
    deleteReq.onsuccess = () => resolve();
    deleteReq.onerror = () =>
      reject(new Error(deleteReq.error?.message || 'Failed to delete database'));
    deleteReq.onblocked = () => {
      console.warn(`${LEGACY_RECORDING_SESSION_DB} deletion blocked, continuing anyway`);
      resolve();
    };
  });
}

/**
 * Helper: Clear keys from both localStorage and sessionStorage
 */
function clearKeysFromBothStorages(
  localKeys: readonly string[],
  sessionKeys: readonly string[],
): string[] {
  const clearedKeys: string[] = [];

  // Clear from localStorage
  localKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
      clearedKeys.push(`localStorage:${key}`);
    } catch (error) {
      console.warn(`Failed to remove localStorage key: ${key}`, error);
    }
  });

  // Clear from sessionStorage
  sessionKeys.forEach(key => {
    try {
      sessionStorage.removeItem(key);
      clearedKeys.push(`sessionStorage:${key}`);
    } catch (error) {
      console.warn(`Failed to remove sessionStorage key: ${key}`, error);
    }
  });

  return clearedKeys;
}

/**
 * Clear encounter-related state
 * Fixes: Encounter not loading, stuck on wrong patient
 * Uses event system to synchronize both localStorage AND React state
 */
// eslint-disable-next-line @typescript-eslint/require-await
export async function clearEncounterState(): Promise<ClearResult> {
  try {
    // Get current activeEncounterId before clearing (for logging)
    let activeEncounterId: string | undefined;
    try {
      const activeEncounterIdRaw = localStorage.getItem('activeEncounterId');
      if (activeEncounterIdRaw) {
        const parsed: unknown = JSON.parse(activeEncounterIdRaw);
        if (typeof parsed === 'string') {
          activeEncounterId = parsed;
        }
      }
    } catch (error) {
      console.warn('Failed to read activeEncounterId for logging:', error);
    }

    // Dispatch event to clear encounter (syncs both localStorage AND React state)
    dispatchClearActiveEncounter({
      reason: 'manual',
      encounterId: activeEncounterId,
    });

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared encounter state',
      level: 'info',
      data: {
        encounterId: activeEncounterId,
        method: 'event-system',
      },
    });

    return {
      success: true,
      keysCleared: ['localStorage:activeEncounterId', 'React:activeEncounterIdState'],
      spaceFreed: 0,
      requiresReload: false,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearEncounterState' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear recording-related state (localStorage + sessionStorage)
 * Fixes: Recording stuck, frozen, or in invalid state, tab sync issues
 * Warning: This will lose current recording progress AND clear tab coordination
 */
// eslint-disable-next-line @typescript-eslint/require-await
export async function clearRecordingState(): Promise<ClearResult> {
  try {
    const category = STORAGE_CATEGORIES.recording;
    const clearedKeys = clearKeysFromBothStorages(category.localStorage, category.sessionStorage);

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared recording state (localStorage + sessionStorage)',
      level: 'info',
      data: {
        keysCleared: clearedKeys.length,
        keys: clearedKeys,
      },
    });

    return {
      success: true,
      keysCleared: clearedKeys,
      spaceFreed: 0,
      requiresReload: false,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearRecordingState' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear Redux persist state
 * Fixes: App behaving oddly after update, state corruption
 * @param keepAuth - If true, preserves auth tokens and user info
 */
// eslint-disable-next-line @typescript-eslint/require-await
export async function clearReduxPersist(keepAuth = true): Promise<ClearResult> {
  try {
    const allKeys = Object.keys(localStorage);
    const persistKeys = allKeys.filter(key => REDUX_PERSIST_PATTERN.test(key));
    const authKeys = keepAuth ? [...STORAGE_CATEGORIES.auth.localStorage] : [];

    let cleared = 0;
    const keysCleared: string[] = [];

    persistKeys.forEach(key => {
      // Skip auth keys if keepAuth is true
      if (keepAuth && authKeys.some(authKey => key.includes(authKey))) {
        return;
      }

      try {
        localStorage.removeItem(key);
        keysCleared.push(`localStorage:${key}`);
        cleared++;
      } catch (error) {
        console.warn(`Failed to remove persist key: ${key}`, error);
      }
    });

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared Redux persist state',
      level: 'info',
      data: {
        keysCleared: cleared,
        keptAuth: keepAuth,
      },
    });

    return {
      success: true,
      keysCleared,
      spaceFreed: 0,
      requiresReload: true,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearReduxPersist' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear cached recording audio from every current and legacy storage backend.
 * Clears OPFS/JotPsychChunkStorage plus SmartScribeAudioChunks and
 * SmartScribeRecordingSessions from the retired storage path.
 * Fixes: Low storage space, orphaned recording sessions
 * Safe: Only clears cached audio data, not user settings or completed notes
 */
export async function clearAudioCache(): Promise<ClearResult> {
  try {
    // Get storage estimate before clearing
    let beforeUsage = 0;
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      beforeUsage = estimate.usage || 0;
    }

    const clearedBackends = await clearPermanentRecordingStorage();
    await clearLegacyRecordingStorage();

    // Get storage estimate after clearing
    let afterUsage = 0;
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      afterUsage = estimate.usage || 0;
    }

    const spaceFreed = beforeUsage - afterUsage;

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared audio cache across permanent and legacy storage',
      level: 'info',
      data: {
        spaceFreed,
        beforeUsage,
        afterUsage,
        permanentBackends: clearedBackends,
        legacyDatabases: ['SmartScribeAudioChunks', LEGACY_RECORDING_SESSION_DB],
      },
    });

    return {
      success: true,
      spaceFreed,
      requiresReload: false,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearAudioCache' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear all storage (nuclear option)
 * Warning: This will log the user out and clear all app state
 */
export async function clearAllStorage(): Promise<ClearResult> {
  try {
    // Clear recording audio from permanent and legacy backends first.
    await clearPermanentRecordingStorage();
    await clearLegacyRecordingStorage();

    // Clear all localStorage
    localStorage.clear();

    // Clear sessionStorage
    sessionStorage.clear();

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared all storage (nuclear option)',
      level: 'warning',
    });

    return {
      success: true,
      requiresReload: true,
      requiresReauth: true,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearAllStorage' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear cache and temporary data
 * Safe: Only clears non-essential cached data
 */
// eslint-disable-next-line @typescript-eslint/require-await
export async function clearCacheData(): Promise<ClearResult> {
  try {
    const category = STORAGE_CATEGORIES.cache;
    const clearedKeys = clearKeysFromBothStorages(category.localStorage, category.sessionStorage);

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared cache data',
      level: 'info',
      data: {
        keysCleared: clearedKeys.length,
      },
    });

    return {
      success: true,
      keysCleared: clearedKeys,
      spaceFreed: 0,
      requiresReload: false,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearCacheData' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear UI preferences and state
 * Safe: Resets filters, tooltips, sidebar settings, onboarding flags
 * Fixes: Stuck filters, broken sidebar, missing tooltips
 */
// eslint-disable-next-line @typescript-eslint/require-await
export async function clearPreferencesAndUI(): Promise<ClearResult> {
  try {
    const preferencesCategory = STORAGE_CATEGORIES.preferences;
    const uiStateCategory = STORAGE_CATEGORIES.uiState;

    const clearedKeys = [
      ...clearKeysFromBothStorages(
        preferencesCategory.localStorage,
        preferencesCategory.sessionStorage,
      ),
      ...clearKeysFromBothStorages(uiStateCategory.localStorage, uiStateCategory.sessionStorage),
    ];

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared UI preferences and state',
      level: 'info',
      data: {
        keysCleared: clearedKeys.length,
        categories: ['preferences', 'uiState'],
      },
    });

    return {
      success: true,
      keysCleared: clearedKeys,
      spaceFreed: 0,
      requiresReload: true, // Reload to apply default UI state
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearPreferencesAndUI' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear OAuth and integration state
 * Use case: Google Calendar sync stuck, OAuth errors
 * May require re-authenticating with third-party services
 */
// eslint-disable-next-line @typescript-eslint/require-await
export async function clearIntegrations(): Promise<ClearResult> {
  try {
    const oauthCategory = STORAGE_CATEGORIES.oauth;
    const moduleCustomizationCategory = STORAGE_CATEGORIES.moduleCustomization;

    const clearedKeys = [
      ...clearKeysFromBothStorages(oauthCategory.localStorage, oauthCategory.sessionStorage),
      ...clearKeysFromBothStorages(
        moduleCustomizationCategory.localStorage,
        moduleCustomizationCategory.sessionStorage,
      ),
    ];

    SentryService.addBreadcrumb({
      category: 'storage.management',
      message: 'Cleared integration state',
      level: 'info',
      data: {
        keysCleared: clearedKeys.length,
        categories: ['oauth', 'moduleCustomization'],
      },
    });

    return {
      success: true,
      keysCleared: clearedKeys,
      spaceFreed: 0,
      requiresReload: true,
    };
  } catch (error) {
    SentryService.captureException(error, {
      level: 'error',
      tags: { component: 'StorageManagement' },
      extra: { method: 'clearIntegrations' },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
